/**
 * ⟦C61 · A-178⟧ **La célébration de fin : les données, pures.**
 *
 * La séquence plein écran (`screens/Fins/Ceremonie.tsx`) joue quatre tableaux de pixel art, dessinés
 * en local (klein9b, `assets/making-of/2026-10-03-C61-celebration/`), sur lesquels le jeu compose
 * ce qui ne peut pas être dans l'image :
 *
 * - **l'avatar du joueur** (ses sprites existants, f ou h), posé à un endroit propre à chaque tableau ;
 * - **le nom de la chaîne** sur la plaque bleue, VIERGE dans l'image du tableau 3 : le nom est
 *   dynamique, jamais de texte généré dans l'image. La plaque est un parallélogramme (le mur est
 *   en perspective isométrique) : le texte lui est posé par une matrice affine `matricePlaque`,
 *   calculée à partir des quatre coins MESURÉS sur l'image (`scripts/mesure-plaque.py`, preuves du
 *   chantier), jamais devinés ;
 * - **une ligne** par tableau, douze mots au plus (Matt, 03/10 : plus de paragraphes).
 *
 * Ce module ne connaît ni React ni le DOM ni l'audio : il se teste seul (`ceremonie.test.ts`).
 * Aucune règle de fin n'est ici : le moteur n'est pas touché, seul l'habillage change.
 */

import type { EndingId, GenreAvatar } from "@maxime/shared";

export type IdTableau = "foule" | "ruban" | "plaque" | "fete";

/** Un point, en pourcentage du tableau (0 = bord gauche/haut, 100 = bord droit/bas). */
export interface Point {
  x: number;
  y: number;
}

/** Les quatre coins de la plaque dans l'image, en % du tableau. */
export interface QuadPlaque {
  hautGauche: Point;
  hautDroit: Point;
  basGauche: Point;
  basDroit: Point;
}

/** Où se pose l'avatar : le milieu de ses pieds (% du tableau) et sa hauteur (% du tableau). */
export interface PlaceAvatar {
  x: number;
  y: number;
  hauteur: number;
  /** De dos : il regarde la plaque, comme à la fenêtre de sa chambre (`av-{genre}-fenetre.png`). */
  dos?: boolean;
  /** Il saute de joie (le dernier tableau). */
  saute?: boolean;
}

export interface Tableau {
  id: IdTableau;
  /** L'image, sous `client/public/`. */
  image: string;
  /** Durée souhaitée en ms, recalée sur les temps de la musique quand il y en a une. */
  dureeMs: number;
  place: PlaceAvatar;
  /** La plaque vierge de l'image (tableaux 3 et 4) : le nom de la chaîne s'y pose. */
  plaque?: QuadPlaque;
  /**
   * La marge laissée au cadre de la plaque (part de chaque côté). Le quad du tableau 3 est le bord
   * EXTÉRIEUR du cadre (il faut rentrer dans le filet blanc), celui du tableau 4 le bleu intérieur.
   */
  margePlaque?: { x: number; y: number };
  /** Pluie de confettis en pixels. */
  confettis?: boolean;
  /**
   * La caméra : un lent travelling avant sur l'image (jamais de coupe sèche). `zoom` va de `de` à
   * `vers` pendant le tableau, autour du point (`x`, `y`) en % du tableau.
   */
  camera: { de: number; vers: number; x: number; y: number };
}

/**
 * Les quatre tableaux. Les images sont des tirages de `klein9b` (FLUX.2, local, zéro crédit) :
 * diorama isométrique sur fond bleu nuit, comme les quatre stills fondateurs de la DA
 * (`assets/stills-8bit/`, ⟦A-133⟧). Les emplacements de l'avatar sont mesurés sur chaque image
 * (un coin de rue vide à l'avant, laissé exprès dans la consigne), pas partagés d'un tableau à l'autre.
 */
export const TABLEAUX: readonly Tableau[] = [
  {
    id: "foule",
    image: "/ceremonie/ceremonie-1-foule.png",
    dureeMs: 4400,
    place: { x: 50, y: 78, hauteur: 25 },
    camera: { de: 1, vers: 1.06, x: 50, y: 60 },
  },
  {
    id: "ruban",
    image: "/ceremonie/ceremonie-2-ruban.png",
    dureeMs: 4600,
    place: { x: 36, y: 83, hauteur: 28 },
    camera: { de: 1, vers: 1.07, x: 52, y: 55 },
  },
  {
    id: "plaque",
    image: "/ceremonie/ceremonie-3-plaque.png",
    dureeMs: 4800,
    place: { x: 50, y: 88, hauteur: 26, dos: true },
    // Mesuré sur le masque du bleu (`mesure-plaque.py --dump`), puis recalé À LA MAIN : la main du
    // maire mord sur le coin bas gauche et le drap sur le bord droit, ce qui fausse les colonnes extrêmes.
    plaque: {
      hautGauche: { x: 39.55, y: 25.59 },
      hautDroit: { x: 62.21, y: 34.47 },
      basGauche: { x: 39.55, y: 44.43 },
      basDroit: { x: 61.72, y: 53.71 },
    },
    // Le tableau de la plaque : on s'approche d'elle, c'est le sujet (et le nom y devient lisible
    // sur un téléphone).
    camera: { de: 1, vers: 1.3, x: 50, y: 40 },
  },
  {
    id: "fete",
    image: "/ceremonie/ceremonie-4-fete.png",
    dureeMs: 5600,
    place: { x: 47, y: 82, hauteur: 28, saute: true },
    plaque: {
      hautGauche: { x: 59.86, y: 26.17 },
      hautDroit: { x: 76.86, y: 35.25 },
      basGauche: { x: 59.86, y: 34.86 },
      basDroit: { x: 76.86, y: 44.24 },
    },
    margePlaque: { x: 0.05, y: 0.09 },
    confettis: true,
    camera: { de: 1.04, vers: 1, x: 50, y: 55 },
  },
];

/**
 * Les confettis : une pluie de carrés, déterministe (aucun `Math.random` au rendu : la cérémonie
 * se rejoue à l'identique, et un test peut la compter). `x` en % de la largeur, `delai` et `duree`
 * en secondes, `taille` en % du côté du tableau (0,7 à 1,4 : un pixel du dessin en fait ~0,5), `derive` en % (le vent), `couleur` un index de 0 à 5.
 */
export interface Confetti {
  x: number;
  delai: number;
  duree: number;
  taille: number;
  derive: number;
  couleur: number;
}

export const NB_COULEURS_CONFETTI = 6;

export function confettis(nombre = 44): Confetti[] {
  const alea = (i: number, n: number) => {
    const v = Math.sin(i * 12.9898 + n * 78.233) * 43758.5453;
    return v - Math.floor(v);
  };
  return Array.from({ length: nombre }, (_, i) => ({
    x: Math.round(alea(i, 1) * 1000) / 10,
    delai: Math.round(alea(i, 2) * 30) / 10,
    duree: 2.6 + Math.round(alea(i, 3) * 25) / 10,
    taille: 0.7 + Math.round(alea(i, 4) * 7) / 10,
    derive: Math.round((alea(i, 5) - 0.5) * 120) / 10,
    couleur: Math.floor(alea(i, 6) * NB_COULEURS_CONFETTI),
  }));
}

/** Ce que les lignes savent de la run. */
export interface ContexteLigne {
  /** ⟦A-74⟧ La fin nommée par le moteur (M3.9) — `null` sur un état d'avant. */
  fin: EndingId | null;
}

/**
 * ⟦A-43⟧ Le jeu dit ce qui se passe, ici il raconte, en une ligne. Univers parodique (CLAUDE.md) :
 * **Castelclic**, la **Mamie**. Douze mots au plus (Matt, 03/10, A-178) : la mesure est dans le test.
 *
 * ⟦A-74⟧ Le dernier tableau porte la fin, comme le dernier acte d'avant : la cérémonie reste
 * célébrée pour tout le monde, mais la foule n'est pas la même selon qu'on a bâti une entreprise,
 * une communauté ou les grands rendez-vous. `fin: null` rend le texte de La Rue.
 */
export function ligneDuTableau(id: IdTableau, ctx: ContexteLigne): string {
  switch (id) {
    case "foule":
      return "Castelclic, 11 h 02. Ils sont tous venus.";
    case "ruban":
      return "Le maire a mis son écharpe. La Mamie a pris sa chaise.";
    case "plaque":
      return "Le drap tombe. La rue porte ton nom.";
    case "fete":
      if (ctx.fin === "ceo") return "Ton équipe applaudit en veste, la Mamie pleure déjà. C'est ta rue.";
      if (ctx.fin === "la_communaute") return "La foule chante un commentaire épinglé. Cette rue est à vous tous.";
      return "Confettis, applaudissements, la Mamie pleure déjà. C'est ta rue.";
  }
}

/** Le nombre de mots d'une ligne — la règle de Matt (12 au plus) se vérifie avec ça. */
export const MOTS_MAX_LIGNE = 12;

export function nbMots(texte: string): number {
  return texte.trim().split(/\s+/).filter((m) => m.length > 0).length;
}

/**
 * Le sprite de l'avatar d'un tableau : debout (`av-{genre}-debout-frais`) ou de dos
 * (`av-{genre}-fenetre`, la planche qui le montre devant sa fenêtre). Sans genre connu (parties
 * d'avant ⟦A-4 §2⟧), le héros R3 du jeu (`sprites/heros-idle|victoire.png`).
 */
export function spriteCeremonie(genre: GenreAvatar | null | undefined, place: PlaceAvatar): string {
  if (!genre) return place.saute ? "/sprites/heros-victoire.png" : "/sprites/heros-idle.png";
  return place.dos ? `/avatar/av-${genre}-fenetre.png` : `/avatar/av-${genre}-debout-frais.png`;
}

/**
 * Les coefficients de l'homographie qui envoie le rectangle (0, 0)-(largeur, hauteur) sur la
 * plaque de l'image (unités du quad). Le mur est en perspective : les côtés de la plaque ne sont
 * pas rigoureusement parallèles dans le tirage (mesuré : 63 px de pente en haut, 82 en bas pour
 * 265 de large), une transformation affine ne suffirait pas, il faut les quatre coins.
 *
 * Formules du carré unité vers un quadrilatère (Heckbert, 1989), puis mise à l'échelle du rectangle.
 * Rend les neuf coefficients `[h11, h12, h13, h21, h22, h23, h31, h32, h33]`.
 */
export function homographie(q: QuadPlaque, largeur: number, hauteur: number): number[] {
  const { x: x0, y: y0 } = q.hautGauche;
  const { x: x1, y: y1 } = q.hautDroit;
  const { x: x2, y: y2 } = q.basDroit;
  const { x: x3, y: y3 } = q.basGauche;
  const dx1 = x1 - x2;
  const dx2 = x3 - x2;
  const dx3 = x0 - x1 + x2 - x3;
  const dy1 = y1 - y2;
  const dy2 = y3 - y2;
  const dy3 = y0 - y1 + y2 - y3;
  let g = 0;
  let h = 0;
  if (Math.abs(dx3) > 1e-9 || Math.abs(dy3) > 1e-9) {
    const det = dx1 * dy2 - dx2 * dy1;
    g = (dx3 * dy2 - dx2 * dy3) / det;
    h = (dx1 * dy3 - dx3 * dy1) / det;
  }
  const a = x1 - x0 + g * x1;
  const b = x3 - x0 + h * x3;
  const d = y1 - y0 + g * y1;
  const e = y3 - y0 + h * y3;
  return [a / largeur, b / hauteur, x0, d / largeur, e / hauteur, y0, g / largeur, h / hauteur, 1];
}

/** Où tombe le point (x, y) du rectangle `largeur × hauteur` une fois plaqué sur le quad. */
export function projeter(hm: readonly number[], x: number, y: number): Point {
  const w = hm[6]! * x + hm[7]! * y + hm[8]!;
  return { x: (hm[0]! * x + hm[1]! * y + hm[2]!) / w, y: (hm[3]! * x + hm[4]! * y + hm[5]!) / w };
}

/**
 * Le `transform: matrix3d(...)` CSS de cette homographie (colonnes d'abord). Appliqué à un bloc de
 * `largeur × hauteur` px, `transform-origin: 0 0`, dans un repère dont l'unité est celle du quad.
 */
export function matrice3d(hm: readonly number[]): string {
  const [h11, h12, h13, h21, h22, h23, h31, h32, h33] = hm as [number, number, number, number, number, number, number, number, number];
  return `matrix3d(${h11},${h21},0,${h31},${h12},${h22},0,${h32},0,0,1,0,${h13},${h23},0,${h33})`;
}

/**
 * Le rectangle utile de la plaque : le bleu est cerclé d'un filet blanc, le texte se pose dedans.
 * `marge` est la part de chaque côté (0 à 0,5) laissée au cadre.
 */
export function zoneUtile(q: QuadPlaque, marge: { x: number; y: number }): QuadPlaque {
  const unite = homographie(q, 1, 1);
  const p = (u: number, v: number) => projeter(unite, u, v);
  return {
    hautGauche: p(marge.x, marge.y),
    hautDroit: p(1 - marge.x, marge.y),
    basGauche: p(marge.x, 1 - marge.y),
    basDroit: p(1 - marge.x, 1 - marge.y),
  };
}

/** Largeur d'un caractère de VT323 (la police pixel du jeu), en em : chasse fixe, 0,4 em. */
export const CHASSE_VT323 = 0.4;

/**
 * Le nom sur une ou deux lignes. Jusqu'à `seuil` caractères : une seule ligne. Au-delà, coupé à
 * l'espace le plus proche du milieu (un nom de 24 caractères est permis, `NOM_MAX`), jamais
 * tronqué : un nom sans espace trop long reste sur une ligne et la police rétrécit.
 */
export function lignesDuNom(nom: string, seuil = 13): string[] {
  const propre = nom.trim().replace(/\s+/g, " ");
  if (propre.length === 0) return ["sans nom"];
  if (propre.length <= seuil) return [propre];
  const milieu = propre.length / 2;
  let meilleur = -1;
  for (let i = 0; i < propre.length; i++) {
    if (propre[i] !== " ") continue;
    if (meilleur === -1 || Math.abs(i - milieu) < Math.abs(meilleur - milieu)) meilleur = i;
  }
  if (meilleur === -1) return [propre];
  return [propre.slice(0, meilleur), propre.slice(meilleur + 1)];
}

/**
 * La taille de police (même unité que `largeur`) pour que la plus longue ligne tienne dans
 * `largeur`, plafonnée à `max` : on rétrécit, on ne coupe jamais.
 */
export function taillePourTenir(lignes: readonly string[], largeur: number, max: number): number {
  const plusLongue = Math.max(1, ...lignes.map((l) => [...l].length));
  return Math.min(max, largeur / (plusLongue * CHASSE_VT323));
}
